import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { ConsultaPopup, useConsultaPopup } from "./components/ConsultaPopup";

import Index from "./pages/Index";

const HairTreatments = lazy(() => import("./pages/HairTreatments"));
const ClinicalDermatology = lazy(() => import("./pages/ClinicalDermatology"));
const Onychology = lazy(() => import("./pages/Onychology"));
const FacialTreatments = lazy(() => import("./pages/FacialTreatments"));
const Teleconsultation = lazy(() => import("./pages/Teleconsultation"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const TreatmentsPage = lazy(() => import("./pages/TreatmentsPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const SmoothScroll = lazy(() => import("./components/SmoothScroll").then(m => ({ default: m.SmoothScroll })));
const FloatingWhatsApp = lazy(() => import("./components/FloatingWhatsApp").then(m => ({ default: m.FloatingWhatsApp })));

const queryClient = new QueryClient();

const PageWrapper = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

const UtmPreserver = () => {
  const location = useLocation();
  
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const isFirstVisit = !sessionStorage.getItem('has_visited');
    
    if (isFirstVisit) {
      sessionStorage.setItem('has_visited', 'true');
      const initialUtms = new URLSearchParams(location.search);
      sessionStorage.setItem('saved_utms', initialUtms.toString());
    }

    const savedUtmsStr = sessionStorage.getItem('saved_utms');
    const savedParams = savedUtmsStr ? new URLSearchParams(savedUtmsStr) : new URLSearchParams();
    let changed = false;

    // Restaura todos os parâmetros originais salvos (utm_source, utm_medium, etc.), exceto utm_content
    savedParams.forEach((value, key) => {
      if (!currentParams.has(key) && key !== 'utm_content') {
        currentParams.set(key, value);
        changed = true;
      }
    });

    // Define o nome da página atual
    const pageName = location.pathname === '/' ? 'home' : location.pathname.replace(/^\//, '').replace(/\//g, '-');
    
    // Define o utm_content APENAS como a página atual
    if (currentParams.get('utm_content') !== pageName) {
      currentParams.set('utm_content', pageName);
      changed = true;
    }

    // Atualiza a URL se houve mudanças para refletir a página atual nos UTMs
    if (changed || (!location.search && currentParams.toString())) {
      window.history.replaceState(null, '', `${location.pathname}?${currentParams.toString()}${location.hash}`);
    }
  }, [location.pathname, location.search, location.hash]);
  
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <Suspense fallback={null}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrapper><Index /></PageWrapper>} />
        <Route path="/tratamentos/tricologia-cabelos" element={<PageWrapper><HairTreatments /></PageWrapper>} />
        <Route path="/tratamentos/dermatologia-clinica" element={<PageWrapper><ClinicalDermatology /></PageWrapper>} />
        <Route path="/tratamentos/onicologia-unhas" element={<PageWrapper><Onychology /></PageWrapper>} />
        <Route path="/tratamentos/acnes-e-cicatrizes" element={<PageWrapper><FacialTreatments /></PageWrapper>} />
        <Route path="/teleconsulta" element={<PageWrapper><Teleconsultation /></PageWrapper>} />
        <Route path="/sobre" element={<PageWrapper><AboutPage /></PageWrapper>} />
        <Route path="/tratamentos" element={<PageWrapper><TreatmentsPage /></PageWrapper>} />
        <Route path="/avaliacoes" element={<PageWrapper><ReviewsPage /></PageWrapper>} />
        <Route path="/blog" element={<PageWrapper><BlogPage /></PageWrapper>} />
        <Route path="/blog/:id" element={<PageWrapper><BlogPostPage /></PageWrapper>} />
        <Route path="/duvidas-frequentes" element={<PageWrapper><FAQPage /></PageWrapper>} />
        <Route path="/mapa-do-site" element={<PageWrapper><SitemapPage /></PageWrapper>} />
        <Route path="/politica-de-privacidade" element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
        <Route path="/termos-de-uso" element={<PageWrapper><TermsOfUse /></PageWrapper>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  const { isOpen, setIsOpen } = useConsultaPopup();

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((window as any).removeHeroShell) {
        (window as any).removeHeroShell();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        <BrowserRouter>
          <UtmPreserver />
          <Suspense fallback={null}>
            <SmoothScroll />
            <FloatingWhatsApp />
          </Suspense>
          <AnimatedRoutes />
          <ConsultaPopup isOpen={isOpen} onOpenChange={setIsOpen} />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
